import React, { useState } from 'react';
import { Check, Sparkles } from 'lucide-react';
import { Card, CardHeader, CardContent, CardFooter } from './ui/card';
import { Badge } from './ui/badge';
import { subscriptions } from '../data/mock';
import { PaymentButton } from './PaymentButton';
import { paymentService } from '../services/paymentService';

export const Subscriptions = () => {

  return (
    <section id="subscriptions" className="py-24 bg-zinc-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-8">
          <Badge className="bg-lime-400/10 text-lime-400 border-lime-400/20 mb-4">
            <Sparkles className="w-3 h-3 mr-1" />
            Save Big with Subscriptions
          </Badge>
          <h2 className="text-4xl sm:text-5xl font-black text-white mb-4">
            Make It a <span className="text-amber-400">Monthly Ritual</span>
          </h2>
          <p className="text-xl text-gray-300">
            Lock in your fuel, save money, and never run out. 
            Switch flavors or cancel anytime — no strings attached.
          </p>
        </div>

        {/* Pioneer Banner */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="bg-gradient-to-r from-amber-400/20 via-lime-400/20 to-amber-400/20 border-2 border-amber-400/40 rounded-xl p-6 text-center">
            <div className="flex items-center justify-center space-x-2 mb-3">
              <span className="text-3xl">🔒</span>
              <h3 className="text-2xl font-black text-white">
                First 50,000 <span className="text-amber-400">PIONEERS</span>
              </h3>
            </div>
            <p className="text-white text-lg mb-2">
              Join LUXE or GOD LEVEL tier and lock in your pricing <span className="font-bold text-lime-400">FOR LIFE</span>
            </p>
            <p className="text-gray-300 text-sm">
              Never worry about price increases. Your rate stays the same forever as a founding member.
            </p>
            <div className="mt-4">
              <Badge className="bg-lime-400 text-black font-bold px-4 py-2 text-sm">
                Limited to First 50,000 Subscribers Only
              </Badge>
            </div>
          </div>
        </div>

        {/* Subscription Plans */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {subscriptions.map((plan) => (
            <Card
              key={plan.id}
              className={`relative overflow-hidden transition-all duration-300 ${
                plan.comingSoon
                  ? 'bg-zinc-800 border-zinc-700 opacity-75'
                  : plan.popular
                  ? 'bg-gradient-to-br from-amber-400/10 to-zinc-900 border-amber-400 lg:scale-105 shadow-2xl'
                  : 'bg-zinc-800 border-zinc-700 hover:border-amber-400/50'
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0 bg-amber-400 text-black px-4 py-1 text-sm font-bold">
                  MOST POPULAR
                </div>
              )}
              {plan.comingSoon && (
                <div className="absolute top-0 right-0 bg-lime-400 text-black px-4 py-1 text-sm font-bold">
                  COMING SOON
                </div>
              )}

              <CardHeader className="pb-6">
                <h3 className="text-2xl font-black text-white mb-2">{plan.name}</h3>
                <p className="text-gray-400 text-sm mb-4">{plan.description}</p>
                <div className="flex items-baseline">
                  <span className="text-5xl font-black text-white">${plan.price}</span>
                  <span className="text-gray-400 ml-2">/{plan.interval}</span>
                </div>
              </CardHeader>

              <CardContent className="pb-6">
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <Check className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                        plan.popular ? 'text-amber-400' : 'text-lime-400'
                      }`} />
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter>
                {plan.comingSoon ? (
                  <button
                    className="w-full font-bold text-lg py-6 bg-zinc-700 text-gray-400 cursor-not-allowed rounded-lg"
                    disabled
                  >
                    Launching Soon
                  </button>
                ) : (
                  <PaymentButton
                    packageId={paymentService.mapSubscriptionToPackageId(plan.id)}
                    productName={plan.name}
                    price={plan.price}
                    requiresAgeVerification={false}
                    className={`w-full font-bold text-lg py-6 ${
                      plan.popular
                        ? 'bg-amber-400 hover:bg-amber-500 text-black'
                        : 'bg-white hover:bg-amber-400 text-black'
                    }`}
                  >
                    {plan.popular ? 'Get Started' : 'Choose Plan'}
                  </PaymentButton>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Bottom Info */}
        <div className="text-center mt-12 space-y-6">
          <p className="text-gray-400">
            All plans include shipping and the flexibility to pause, skip, or cancel anytime.
            <br />
            <span className="text-white font-semibold">No hidden fees. No nonsense.</span>
          </p>
          
          {/* Legal Disclaimer */}
          <div className="max-w-3xl mx-auto bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
            <p className="text-xs text-gray-400 leading-relaxed">
              <span className="font-semibold text-white">AGE VERIFICATION REQUIRED:</span> Subscription boxes containing 
              mixed drink products require age verification at delivery. You must be 21+ years of age to receive alcoholic beverages. 
              By subscribing, you certify you meet the legal drinking age in your state. Non-compliance will result in subscription cancellation.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};